import React, { useContext, useMemo } from 'react';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import TextInput from '../../../Common/TextInput';
import { Checkbox } from '../../../../components/Checkbox';
import {
  CompanyConnection,
  CompanyConnectionEntityType,
} from '../../../../../types/companyConnection/companyConnection';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CompanyConnectionContext } from '../../../../../context/CompanyConnectionContext';

interface CompanyConnectionInfoPaneProps {
  item: CompanyConnection;
  setItem: (item: CompanyConnection) => void;

  loading: boolean;
}

export default function CompanyConnectionInfoPane({ item, setItem, loading }: CompanyConnectionInfoPaneProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { companyConnections, setCompanyConnections } = useContext(CompanyConnectionContext);

  const types = useMemo(() => {
    return new Set([...item.types]);
  }, [item.types]);

  const isCreate = !item.id;

  return (
    <div className='grid grid-cols-1 gap-2'>
      <div>
        <TextInput
          mandatory
          disabled={!isCreate}
          label='Company ID'
          placeholder='Company ID'
          value={item.companyId === item.parentId ? '' : item.companyId}
          onChange={v => setItem(item.withCompanyId(v))}
        />
      </div>
      <div className='grid gap-1'>
        <Checkbox
          label={t(TranslationKey.productMasterData).plural.label}
          disabled={
            types.has(CompanyConnectionEntityType.products) ||
            types.has(CompanyConnectionEntityType.lots) ||
            types.has(CompanyConnectionEntityType.assets)
          }
          value={types.has(CompanyConnectionEntityType.product_master_data)}
          onChange={v => setItem(item.withProductMasterData(v))}
        />
        <Checkbox
          label={t(TranslationKey.stockLocation).plural.label}
          disabled={types.has(CompanyConnectionEntityType.bins)}
          value={types.has(CompanyConnectionEntityType.stock_locations)}
          onChange={v => setItem(item.withStockLocations(v))}
        />
        <Checkbox
          label={t(TranslationKey.bin).plural.label}
          disabled={
            types.has(CompanyConnectionEntityType.products) || types.has(CompanyConnectionEntityType.containers)
          }
          value={types.has(CompanyConnectionEntityType.bins)}
          onChange={v => setItem(item.withBins(v))}
        />
        <Checkbox
          label={t(TranslationKey.lot).plural.label}
          disabled={
            types.has(CompanyConnectionEntityType.orders) ||
            types.has(CompanyConnectionEntityType.products) ||
            types.has(CompanyConnectionEntityType.product_transactions)
          }
          value={types.has(CompanyConnectionEntityType.lots)}
          onChange={v => setItem(item.withLots(v))}
        />
        <Checkbox
          label={t(TranslationKey.container).plural.label}
          disabled={
            types.has(CompanyConnectionEntityType.orders) ||
            types.has(CompanyConnectionEntityType.products) ||
            types.has(CompanyConnectionEntityType.product_transactions)
          }
          value={types.has(CompanyConnectionEntityType.containers)}
          onChange={v => setItem(item.withContainers(v))}
        />
        <Checkbox
          label={t(TranslationKey.order).plural.label}
          value={types.has(CompanyConnectionEntityType.orders)}
          onChange={v => setItem(item.withOrders(v))}
        />
        <Checkbox
          label={t(TranslationKey.task).plural.label}
          value={types.has(CompanyConnectionEntityType.tasks)}
          onChange={v => setItem(item.withTasks(v))}
        />
        <Checkbox
          label={t(TranslationKey.stock).singular.label}
          value={types.has(CompanyConnectionEntityType.products)}
          onChange={v => setItem(item.withProducts(v))}
        />
        <Checkbox
          label={t(TranslationKey.productTransaction).singular.label}
          value={types.has(CompanyConnectionEntityType.product_transactions)}
          onChange={v => setItem(item.withProductTransactions(v))}
        />
        <Checkbox
          label={t(TranslationKey.asset).singular.label}
          value={types.has(CompanyConnectionEntityType.assets)}
          onChange={v => setItem(item.withAssets(v))}
        />
      </div>
    </div>
  );
}
